import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Loader,
  Segment,
  Header,
  Label,
  Breadcrumb,
  Button,
  Icon,
  Table,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../api';
import EmailDocModal from './docs/EmailDocModal';
import { MARKDOWN_BODY_CSS } from './docs/markdownBodyCss';
import markdownComponents from './markdownComponents';

const CONTENT_TYPE_ICONS = {
  document: 'file text',
  'source-code': 'code',
  presentation: 'desktop',
  specification: 'clipboard',
  research: 'lab',
  binary: 'file',
};

const SOURCE_DIR_LABELS = {
  docs: 'Documentation',
  heritage: 'Heritage',
  'source-archive': 'Source Archive',
  'nick-backend': 'Nick Backend',
};

/** Lazy-load react-markdown + remark-gfm (they're ESM-only) */
let ReactMarkdownComponent = null;
let remarkGfmPlugin = null;
let mdLoaded = false;
let mdLoadPromise = null;

function loadMarkdownDeps() {
  if (mdLoadPromise) return mdLoadPromise;
  mdLoadPromise = Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]).then(([rm, rgfm]) => {
    ReactMarkdownComponent = rm.default;
    remarkGfmPlugin = rgfm.default;
    mdLoaded = true;
  });
  return mdLoadPromise;
}

/** Content-aware body renderer */
const DocBody = ({ doc, mdReady }) => {
  if (!doc.body_md) {
    // Binary files — metadata card only
    return (
      <Segment placeholder textAlign="center">
        <Header icon>
          <Icon name={CONTENT_TYPE_ICONS[doc.content_type] || 'file'} />
          {doc.title}
        </Header>
        <p>
          This is a {doc.content_type} file ({doc.file_extension}).
          {doc.file_size && <> Size: {(doc.file_size / 1024).toFixed(1)} KB.</>}
        </p>
        <p style={{ color: '#888' }}>
          Binary files are catalogued but not viewable in the browser.
        </p>
      </Segment>
    );
  }

  // Source code files — monospace pre block
  if (doc.content_type === 'source-code') {
    return (
      <Segment style={{ overflow: 'auto' }}>
        <pre
          style={{
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            fontSize: '0.9em',
            lineHeight: '1.5',
            whiteSpace: 'pre',
            margin: 0,
            tabSize: 2,
          }}
        >
          {doc.body_md}
        </pre>
      </Segment>
    );
  }

  // Markdown / research / document — react-markdown
  if (ReactMarkdownComponent && mdReady) {
    return (
      <Segment style={{ fontSize: '1.05em', lineHeight: '1.6' }}>
        <style>{MARKDOWN_BODY_CSS}</style>
        <div className="doc-viewer-md md-body">
          <ReactMarkdownComponent remarkPlugins={[remarkGfmPlugin]} components={markdownComponents}>
            {doc.body_md}
          </ReactMarkdownComponent>
        </div>
      </Segment>
    );
  }

  // Fallback pre
  return (
    <Segment>
      <pre style={{ whiteSpace: 'pre-wrap' }}>{doc.body_md}</pre>
    </Segment>
  );
};

const DocViewer = () => {
  const params = useParams();
  const slug = params.slug || params[0] || '';
  const [doc, setDoc] = useState(null);
  const [activeDraft, setActiveDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mdReady, setMdReady] = useState(mdLoaded);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (!mdLoaded) {
      loadMarkdownDeps().then(() => setMdReady(true));
    }
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    // Fetch document + version info in parallel
    Promise.all([
      fetchApi(`${getApiPath()}/@docs/view?slug=${encodeURIComponent(slug)}`),
      fetch(`${getApiPath()}/@docs/versions?slug=${encodeURIComponent(slug)}`, {
        headers: getAuthHeaders(),
      }).then((res) => (res.ok ? res.json() : null)).catch(() => null),
    ])
      .then(([docJson, versionsJson]) => {
        setDoc(docJson);
        if (versionsJson) {
          setActiveDraft(versionsJson.active_draft || null);
          setVersions(versionsJson.versions || []);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading || !mdReady) {
    return (
      <Container id="polyglot-doc-viewer" style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading document..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container id="polyglot-doc-viewer" style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  if (!doc) {
    return (
      <Container id="polyglot-doc-viewer" style={{ paddingTop: '2em' }}>
        <Segment>Document not found.</Segment>
      </Container>
    );
  }

  // Breadcrumb
  const breadcrumbSections = [
    { key: 'docs', content: 'Knowledge Base', as: Link, to: '/docs' },
  ];
  if (doc.source_dir && doc.source_dir !== 'docs') {
    breadcrumbSections.push({
      key: 'source',
      content: SOURCE_DIR_LABELS[doc.source_dir] || doc.source_dir,
      as: Link,
      to: `/docs?source_dir=${encodeURIComponent(doc.source_dir)}`,
    });
  }
  if (doc.category && doc.category !== 'root') {
    breadcrumbSections.push({
      key: 'cat',
      content: doc.category,
      as: Link,
      to: `/docs?category=${encodeURIComponent(doc.category)}`,
    });
  }
  if (doc.subcategory) {
    breadcrumbSections.push({ key: 'sub', content: doc.subcategory });
  }
  breadcrumbSections.push({ key: 'title', content: doc.title, active: true });

  const docTags = doc.tags || [];
  const docCollections = doc.collections || [];

  return (
    <Container
      id="polyglot-doc-viewer"
      style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '900px' }}
    >
      <Helmet title={`${doc.title} - LJUSA TIDER Knowledge Base`} />

      <Breadcrumb
        icon="right angle"
        sections={breadcrumbSections}
        style={{ marginBottom: '1em' }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Header as="h1">
          <Icon
            name={CONTENT_TYPE_ICONS[doc.content_type] || 'file'}
            style={{ opacity: 0.5 }}
          />
          <Header.Content>
            {doc.title}
            {doc.version && <Header.Subheader>v{doc.version}</Header.Subheader>}
          </Header.Content>
        </Header>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <Button
            size="small"
            basic
            onClick={() => setEmailOpen(true)}
            title="Email this doc (the Markdown source, plus its PDF rendering when one is stored)"
          >
            <Icon name="mail" /> Email
          </Button>
          <Button
            as={Link}
            to={`/docs/${slug}/edit`}
            primary
            size="small"
          >
            <Icon name="edit" /> Edit
          </Button>
        </div>
      </div>

      <EmailDocModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        doc={doc}
      />

      {/* Metadata bar */}
      <div style={{ marginBottom: '1em' }}>
        {doc.doc_id && (
          <Label color="blue" style={{ marginRight: '0.5em' }}>{doc.doc_id}</Label>
        )}
        <Label color="blue">{doc.category}</Label>
        {doc.subcategory && <Label>{doc.subcategory}</Label>}
        {doc.source_dir && doc.source_dir !== 'docs' && (
          <Label color="grey">{SOURCE_DIR_LABELS[doc.source_dir] || doc.source_dir}</Label>
        )}
        {doc.project && <Label color="teal">{doc.project}</Label>}
        {doc.status && <Label color="olive">{doc.status}</Label>}
        {doc.content_type && (
          <Label basic>
            <Icon name={CONTENT_TYPE_ICONS[doc.content_type] || 'file'} />
            {doc.content_type}
          </Label>
        )}
        {doc.file_extension && <Label basic>{doc.file_extension}</Label>}
        {doc.author && (
          <span style={{ marginLeft: '1em', color: '#555' }}>
            <Icon name="user" />{doc.author}
          </span>
        )}
        {doc.doc_date && (
          <span style={{ marginLeft: '1em', color: '#555' }}>
            <Icon name="calendar" />{doc.doc_date}
          </span>
        )}
      </div>

      {/* Tags */}
      {docTags.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          {docTags.map((t) => (
            <Label
              key={t.code}
              as={Link}
              to={`/docs?tag=${encodeURIComponent(t.code)}`}
              size="small"
              color={t.color || undefined}
            >
              {t.label}
            </Label>
          ))}
        </div>
      )}

      {/* Collections */}
      {docCollections.length > 0 && (
        <div style={{ marginBottom: '1.5em' }}>
          {docCollections.map((c) => (
            <Label
              key={c.code}
              as={Link}
              to={`/docs?collection=${encodeURIComponent(c.code)}`}
              size="small"
              basic
            >
              {c.icon && <Icon name={c.icon} />}
              {c.title}
            </Label>
          ))}
        </div>
      )}

      {/* Draft in progress banner */}
      {activeDraft && (
        <Segment color="blue" style={{ marginBottom: '1em' }}>
          <Icon name="edit" />
          <strong>Draft in progress:</strong> Version {activeDraft.version} is being
          {activeDraft.status === 'in_review' ? ' reviewed' : ' edited'}.
          {' '}
          <Link to={`/docs/${slug}/edit`}>Open editor</Link>
          {activeDraft.status === 'in_review' && (
            <>
              {' | '}
              <Link to={`/docs/${slug}/review`}>Review draft</Link>
            </>
          )}
        </Segment>
      )}

      {/* Document body — content-aware */}
      <DocBody doc={doc} mdReady={mdReady} />

      {/* Git footer */}
      <Segment secondary size="small">
        <Icon name="git" />
        {doc.git_hash && (
          <span>
            Commit: <code>{doc.git_hash.substring(0, 8)}</code>
          </span>
        )}
        {doc.git_author && <span> by {doc.git_author}</span>}
        {doc.git_date && (
          <span> on {new Date(doc.git_date).toLocaleDateString()}</span>
        )}
        {doc.file_size && (
          <span style={{ marginLeft: '1.5em' }}>
            {(doc.file_size / 1024).toFixed(1)} KB
          </span>
        )}
        <span style={{ float: 'right', color: '#888' }}>
          Synced: {new Date(doc.synced_at).toLocaleString()}
        </span>
      </Segment>

      {/* Revision History — inline table matching QMS register presentation */}
      {versions.length > 0 && (
        <>
          <Header as="h3" style={{ marginTop: '1.5em' }}>Revision History</Header>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Author</Table.HeaderCell>
                <Table.HeaderCell>Changes</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {versions.map((v) => (
                <Table.Row key={v.id}>
                  <Table.Cell>
                    <Link to={`/docs/${slug}/version?v=${encodeURIComponent(v.version)}`}>
                      <strong>v{v.version}</strong>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(v.published_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>{v.author || '—'}</Table.Cell>
                  <Table.Cell>{v.changes || '—'}</Table.Cell>
                  <Table.Cell>
                    {v.superseded_at ? (
                      <Label size="tiny" color="grey">
                        Superseded &rarr; v{v.superseded_by}
                      </Label>
                    ) : (
                      <Label size="tiny" color="green">
                        Effective
                      </Label>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </Container>
  );
};

export default DocViewer;
